import {
    CACHE_STORAGE_PREFIX, createExpiringKeyTest, describeStoredValue, ICachedItem, IExpiringEnvelope, isLiveEnvelope, parseStoredItem, sweepExpiredItems,
} from '../../../shared/frontend/storageEnvelope';
import { FEATURE_FLAGS_EXPIRATION_IN_SECONDS } from '../../../shared/interfaces/api/apiInterfaces';
import { getClLogger } from '../../../shared/util/clLogger';
import { CacheScope, KEY_POSITION } from '../interfaces/static';
import { getUser } from '../layout';

export const FILTERS_DEFAULT_SCOPE = CacheScope.User;
export const SELECT_GROUP_DEFAULT_SCOPE = CacheScope.Tab;
export const TREE_DEFAULT_SCOPE = CacheScope.Tab;

// Utilities for interacting with local storage in a consistent way
const clLogger = getClLogger(__filename);
const CACHE_MAX_TIME = 60000 * 3;
// Store DM-related items for a week to be safe
const CACHE_DM_MAX_TIME = 60000 * 10080;

// One registered storage key. `position` says where `key` sits in the stored name when callers append their own part
// (a filter page, a conversation id); `cacheMaxTime` marks an expiring key, whose value setCacheKey wraps in an ICachedItem envelope.
export interface IStorageKeyInfo {
    scope: CacheScope;
    key: string;
    position?: KEY_POSITION;
    cacheMaxTime?: number;
}

// What readThroughCacheIDBDatabase stores per key in IndexedDB.
interface ICachedIDBItem<T> extends IExpiringEnvelope {
    key: IDBValidKey;
    data: T;
}

// When adding a new key make sure it won't colide with another
export const STORAGE_INFO = {
    clJwt: { scope: CacheScope.Global, key: 'clJwt' },
    clLoginConfig: { scope: CacheScope.Global, key: 'clLoginConfig' },
    clLastUser: { scope: CacheScope.Global, key: 'clLastUser' },
    clTempUser: { scope: CacheScope.Global, key: 'clTempUser' },
    clEvents: { scope: CacheScope.Global, key: 'clEvents' },
    clApi: { scope: CacheScope.Global, key: 'clApi' },
    debug: { scope: CacheScope.Global, key: 'debug' },
    clFeatureFlags: { scope: CacheScope.Global, key: 'CL_FEATURE_FLAGS', cacheMaxTime: FEATURE_FLAGS_EXPIRATION_IN_SECONDS * 1000 },
    analysisCustomSymbols: { scope: CacheScope.Tab, key: 'analysis-customSymbols' },
    clParsepipeAssets: { scope: CacheScope.Tab, key: 'clParsepipeAssets' },
    msal: { scope: CacheScope.Global, key: 'msal.', position: KEY_POSITION.prefix },
    clHomeBookmark: { scope: CacheScope.User, key: 'clHomeBookmark', position: KEY_POSITION.suffix },
    timeMachineDate: { scope: CacheScope.User, key: 'timeMachineDate', position: KEY_POSITION.suffix },
    addArticlePublisher: { scope: CacheScope.User, key: 'addArticlePublisher' },
    clGraphNodePrefs: { scope: CacheScope.User, key: 'cl-graph-nodePrefs', position: KEY_POSITION.suffix },
    dashboardTourAlreadyAutoRan: { scope: CacheScope.User, key: 'dashboardTourAlreadyAutoRan,', position: KEY_POSITION.suffix },
    // filters
    indicatorsFilters: { scope: FILTERS_DEFAULT_SCOPE, key: 'indicatorsFilters', position: KEY_POSITION.prefix },
    driversFilters: { scope: FILTERS_DEFAULT_SCOPE, key: 'driversFilters', position: KEY_POSITION.prefix },
    eventsFilters: { scope: FILTERS_DEFAULT_SCOPE, key: 'eventsFilters', position: KEY_POSITION.prefix },
    signalFilters: { scope: FILTERS_DEFAULT_SCOPE, key: 'signalFilters', position: KEY_POSITION.prefix },
    modelFilters: { scope: FILTERS_DEFAULT_SCOPE, key: 'modelFilters', position: KEY_POSITION.prefix },
    // DMs
    conversation: { scope: CacheScope.User, key: 'conversation:', position: KEY_POSITION.middle, cacheMaxTime: CACHE_DM_MAX_TIME },
    conversations: { scope: CacheScope.User, key: 'conversations', position: KEY_POSITION.suffix, cacheMaxTime: CACHE_DM_MAX_TIME },
    newMessages: { scope: CacheScope.User, key: 'newMessages', position: KEY_POSITION.suffix, cacheMaxTime: CACHE_DM_MAX_TIME },
    messages: { scope: CacheScope.User, key: 'messages', position: KEY_POSITION.suffix, cacheMaxTime: CACHE_DM_MAX_TIME },
    users: { scope: CacheScope.User, key: 'users', position: KEY_POSITION.suffix, cacheMaxTime: CACHE_DM_MAX_TIME },
    notificationCount: { scope: CacheScope.User, key: 'notificationCount', position: KEY_POSITION.suffix, cacheMaxTime: CACHE_DM_MAX_TIME },
    dashNotificationCount: { scope: CacheScope.User, key: '_notificationCount', position: KEY_POSITION.suffix, cacheMaxTime: CACHE_DM_MAX_TIME },
    // Store off-page support setting for 9 hours
    offPageDMs: { scope: CacheScope.User, key: 'offPageDMs', position: KEY_POSITION.suffix, cacheMaxTime: 60000 * 540 },
    // Announcements
    clNotifyMsg: { scope: CacheScope.User, key: 'clNotifyMsg', position: KEY_POSITION.suffix },
    // Tree
    treeLoadedItems: { scope: TREE_DEFAULT_SCOPE, key: '-loadedItems', position: KEY_POSITION.suffix },
    treeCheckedItems: { scope: TREE_DEFAULT_SCOPE, key: '-checkedItems', position: KEY_POSITION.suffix },
    treeIndeterminateItems: { scope: TREE_DEFAULT_SCOPE, key: '-indeterminateItems', position: KEY_POSITION.suffix },
} satisfies Record<string, IStorageKeyInfo>;

// Keys without a cacheMaxTime hold permanent values; every other key under the CL: prefix holds an expiring envelope.
const permanentEntries = (Object.values(STORAGE_INFO) as IStorageKeyInfo[]).filter((info) => !info.cacheMaxTime);
const permanentNames = (user: boolean, position?: KEY_POSITION) =>
    permanentEntries.filter((info) => (info.scope === CacheScope.User) === user && info.position === position).map((info) => info.key);
const isExpiringKey = createExpiringKeyTest({
    names: permanentNames(false),
    prefixes: permanentNames(false, KEY_POSITION.prefix),
    suffixes: permanentNames(false, KEY_POSITION.suffix),
    userNames: permanentNames(true),
    userPrefixes: permanentNames(true, KEY_POSITION.prefix),
    userSuffixes: permanentNames(true, KEY_POSITION.suffix),
    userMiddles: permanentNames(true, KEY_POSITION.middle),
});

// Proper usage of storage options:
// Local storage is global (spans tabs and windows) per hostname. Use it for caching values that would not vary between tabs and are effectively permanent.
// For local storage, we can also prefix the username (User scope) in the key so that two users of the same browser across two sessions will never see data from the prior session.
// Session storage is scoped per window/tab and therefore should be used for any widgets that might exist more than once at a time, for one user.

// When the user is logged in we will use their username so that individual preferences
// are not overriding other users of the same browser.
function getPrefix(scope: CacheScope): string {
    let start = CACHE_STORAGE_PREFIX;
    if (scope === CacheScope.User) {
        try {
            start += `${getUser().id}:`;
        }
        catch (e) {
            clLogger.error('Local storage by user cannot be used prior to login or outside a session.');
            throw e;
        }
    }
    return start;
}

function storageFor(scope: CacheScope): Storage {
    return scope === CacheScope.Tab ? sessionStorage : localStorage;
}

// Null when the key is absent, unreadable, or an expiring key whose envelope has expired or is missing.
export function fetchCacheKey<T>(scope: CacheScope, key: string): T | null {
    const prefixedKey = `${getPrefix(scope)}${key}`;
    const raw = storageFor(scope).getItem(prefixedKey);
    if (raw === null) {
        return null;
    }
    const parsed = parseStoredItem(raw);
    if (!parsed.readable) {
        clLogger.error(`Ignoring ${scope} cache key ${key}: not JSON (${parsed.error?.message}). Value is: ${describeStoredValue(raw)}`);
        return null;
    }
    if (!isExpiringKey(prefixedKey)) {
        return parsed.item as T;
    }
    return isLiveEnvelope(parsed.item) ? (parsed.item as ICachedItem<T>).value : null;
}

// Preferred method of setting a value for a key will take care of serialization as JSON if necessary.
// cacheMaxTime = 0 means no time control
export function setCacheKey<T>(scope: CacheScope, key: string, value: T, cacheMaxTime?: number): void {
    if (value !== null && value !== undefined) {
        const prefixedKey = `${getPrefix(scope)}${key}`;
        const envelope: ICachedItem<T> = { value, cacheMaxTime, createdTimestamp: Date.now() };
        storageFor(scope).setItem(prefixedKey, JSON.stringify(isExpiringKey(prefixedKey) ? envelope : value));
    }
    else {
        throw Error('Do not set cache keys to null or undefined - use the remove method');
    }
}

// Delete / remove a key, if it exists.
export function removeCacheKey(scope: CacheScope, key: string): void {
    storageFor(scope).removeItem(`${getPrefix(scope)}${key}`);
}

// Convenience for quickly adding a value to a set, if not already present.
// This is really only intended for small lists of simple strings
// If you need a larger object or set of logic, implement separately after fetching the item
export function addToCacheSet(scope: CacheScope, key: string, value: string, cacheMaxTime?: number): void {
    const current = fetchCacheKey<{ [ key: string ]: boolean }>(scope, key) || {};
    if (!current[value]) {
        current[value] = true;
        setCacheKey(scope, key, current, cacheMaxTime);
    }
}

// Removes a particular value from a set, if it exists
export function removeFromCacheSet(scope: CacheScope, key: string, value: string, cacheMaxTime?: number): void {
    const current = fetchCacheKey<{ [ key: string ]: boolean }>(scope, key);
    if (current && current[value]) {
        delete current[value];
        setCacheKey(scope, key, current, cacheMaxTime);
    }
}

export function fetchCacheSet(scope: CacheScope, key: string): string[] {
    const current = fetchCacheKey<{ [ key: string ]: boolean }>(scope, key) || {};
    return Object.keys(current);
}

// Quick test to see if a value in a cached / serialized set has some value in the dictionary
export function cacheSetHasValue(scope: CacheScope, key: string, value: string): boolean {
    const current = fetchCacheKey<{ [ key: string ]: boolean }>(scope, key);
    return (current && current[value]) || false;
}

// Runs every minute from layout.ts. Removes expired items from IndexedDB and from both Web Storages; never throws, and one
// bad item never stops the rest of the sweep (see sweepExpiredItems for what is logged).
export async function clearOldCache() {
    try {
        const objectStore = await openIDBDatabase();
        if (objectStore) {
            const getAllKeysRequest = objectStore.getAllKeys();
            getAllKeysRequest.onsuccess = async () => {
                for (const key of getAllKeysRequest.result) {
                    const data = await getCacheIDBDatabase(key);
                    if (!data) {
                        await deleteCacheIDBDatabase(key);
                    }
                }
            };
        }
    }
    catch (error) {
        clLogger.error('Could not sweep the IndexedDB cache', error);
    }
    sweepExpiredItems(sessionStorage, 'sessionStorage', isExpiringKey);
    sweepExpiredItems(localStorage, 'localStorage', isExpiringKey);
}

// ------------------------------IDBDatabase------------------------------

async function deleteCacheIDBDatabase(key: IDBValidKey) {
    const objectStore = await openIDBDatabase();
    const deleteRequest = objectStore.delete(key);
    deleteRequest.onsuccess = () => {
        // needed?
    };
}

function openIDBDatabase(): Promise<IDBObjectStore> {
    let objectStore: IDBObjectStore;
    return new Promise((resolve) => {
        if (objectStore) {
            resolve(objectStore);
        }
        let db: IDBDatabase;
        const request = window.indexedDB.open('database', 1);

        request.onerror = () => {
            return resolve(null);
        };

        request.onsuccess = () => {
            db = request.result;
            objectStore = db.transaction([ 'json' ], 'readwrite').objectStore('json');
            return resolve(objectStore);
        };

        request.onupgradeneeded = () => {
            db = request.result;
            const newObjectstore = db.createObjectStore('json', { keyPath: 'key' });
            newObjectstore.createIndex('json', 'key', { unique: true });
        };
    });
}

async function setCacheIDBDatabase(key: IDBValidKey, data: any, cacheMaxTime: number = CACHE_MAX_TIME) {
    if (!window.indexedDB) {
        return;
    }
    const storedData = await navigator.storage.estimate();
    let length: number;
    try {
        // according to firefox the size is around 3 times the string length
        length = JSON.stringify(data).length * 3;
    }
    catch (err) {
        clLogger.debug(err);
        return;
    }
    const isFirefox = navigator.userAgent.includes('Firefox');
    // firefox have a max item size but is not in the navigator.storage info
    const FIREFOX_MAX_SIZE = 170000000;
    if (isFirefox && length > FIREFOX_MAX_SIZE) {
        return;
    }
    // check used quota against length
    if (storedData.quota - storedData.usage < length) {
        return;
    }
    const objectStore = await openIDBDatabase();
    if (objectStore) {
        const record: ICachedIDBItem<any> = { key, data, cacheMaxTime, createdTimestamp: Date.now() };
        objectStore.put(record).onsuccess = () => {
            // needed?
        };
    }
}

function getCacheIDBDatabase<T>(key: IDBValidKey): Promise<T> {
    if (!window.indexedDB) {
        return null;
    }
    return new Promise((resolve) => {
        openIDBDatabase().then((objectStore) => {
            if (!objectStore) {
                return resolve(null);
            }
            const dataReq: IDBRequest<ICachedIDBItem<T> | undefined> = objectStore.get(key);
            dataReq.onsuccess = () => {
                const record = dataReq.result;
                return resolve(isLiveEnvelope(record) ? record.data : null);
            };
            return;
        }).catch((err) => {
            clLogger.error(err);
        });
    });
}

export async function readThroughCacheIDBDatabase<T>(key: IDBValidKey, missFunction: () => T, cacheMaxTime?: number, cacheNull: boolean = true) {
    const cachedData = await getCacheIDBDatabase<T>(key);
    let data: T = null;
    if (cachedData) {
        data = cachedData;
    }
    else {
        data = await missFunction();
        // allow to avoid cache if data is null
        if (cacheNull || data) {
            await setCacheIDBDatabase(key, data, cacheMaxTime);
        }
    }
    return data;
}

export async function cleanPartialKeys(part: string) {
    if (part) {
        const objectStore = await openIDBDatabase();
        if (objectStore) {
            const getAllKeysRequest = objectStore.getAllKeys();
            getAllKeysRequest.onsuccess = async () => {
                for (const key of getAllKeysRequest.result) {
                    if (key.toString().includes(part)) {
                        await deleteCacheIDBDatabase(key);
                    }
                }
            };
        }
    }
}
